import { FC, useState } from 'react';
import { Loader2, Trash2 } from 'lucide-react';
import { PlanCode, SubscriptionDetailsDto, SubscriptionStatus } from '@zaplify/subscriptions';
import { useChargebeeSubscription } from '../hooks/use-chargebee-subscription';
import { SubscriptionDetails } from './components/subscription-details';
import { BillingDetails } from './components/billing-details';
import { CancelSubscriptionDialog } from './components/dialogs/cancel-subscription-dialog';
import { RenewalSubscriptionDialog } from './components/dialogs/renewal-subscription-dialog';
import { PickPlanDialog } from './components/dialogs/pick-plan-dialog';
import { useParams, useNavigate } from 'react-router-dom';
import { SuccessDialog } from './components/dialogs/success-dialog';
import { useAuth } from '../../../providers/authentication-provider';
import { getZaplifySdk } from '@zaplify/sdk';
import { useToast } from '@shadcn/ui/hooks/use-toast';
import { Card, CardContent, CardHeader, CardTitle } from '@shadcn/ui/components/ui/card';
import { Button } from '@shadcn/ui/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@shadcn/ui/components/ui/dialog';
import { Input } from '@shadcn/ui/components/ui/input';
import { Label } from '@shadcn/ui/components/ui/label';
import { paths } from '../../../../routes/paths';

type ModalType = 'renew-plan' | 'cancel-plan' | 'upgrade-plan' | 'success' | 'none';

export const SubscriptionPage: FC = () => {
    const {
        subscription,
        isLoadingSubscription,
        loadSelfServicePortal,
        requestSubscriptionCancel,
        refetchSubscription,
    } = useChargebeeSubscription();
    const {
        reloadClaims,
        authState: { userOrganizationId, userId, isGlobalAdmin, user },
    } = useAuth();
    // const [activeModal, setActiveModal] = useState<ModalType>('none');
    const { activeModal } = useParams<{ activeModal: ModalType }>();
    const navigate = useNavigate();
    const { toast } = useToast();

    // Delete organization state
    const [isDeleteOrganizationOpen, setIsDeleteOrganizationOpen] = useState(false);
    const [confirmationText, setConfirmationText] = useState('');
    const [isDeleting, setIsDeleting] = useState(false);

    const organizationName = user?.userOrganization?.name || 'your workspace';

    if (isLoadingSubscription) {
        return (
            <div className="flex items-center justify-center h-full">
                <Loader2 className="w-6 h-6 animate-spin" />
            </div>
        );
    }

    if (!subscription) {
        return <p>No subscription found</p>;
    }

    const subscriptionDetails = subscription as SubscriptionDetailsDto;
    const isEnterprise = subscriptionDetails.subscription?.current?.planCode === PlanCode.ENTERPRISE;
    const isLegacy = subscriptionDetails.subscription?.isLegacy;
    const isLockedFromSelfService = isEnterprise || isLegacy;

    const handleShowModal = (type: ModalType) => {
        navigate(`./${type}`);
    };

    const handleCloseModal = () => {
        navigate('..', { relative: 'path' });
    };

    const handleCancelSubscription = async () => {
        try {
            await requestSubscriptionCancel();
            refetchSubscription().then(() => {
                handleCloseModal();
            });
        } catch (error) {
            console.error('Failed to cancel subscription:', error);
        }
    };

    const handleRenewSubscription = async () => {
        const profilesSdk = getZaplifySdk().profiles;
        const upgradePlanDTO = {
            numberOfSeats: subscriptionDetails.subscription.current.seatCount,
            billingPeriod: {
                amount: subscriptionDetails.subscription.current.periodAmount,
                unit: subscriptionDetails.subscription.current.periodUnit,
            },
            creditNumber: subscriptionDetails.subscription.current.credits,
            planCode: subscriptionDetails.subscription.current.planCode,
            couponCode: '',
            oneTimeCredits: 0,
        };

        try {
            await profilesSdk.subscription.requestSubscriptionUpdate(userOrganizationId, upgradePlanDTO);
            toast({
                title: 'Plan renewed successfully',
                description: 'Your plan has been renewed successfully',
                variant: 'success',
            });
        } catch (error) {
            toast({
                title: 'Failed to renew plan',
                description: 'Please try again',
                variant: 'error',
            });
        } finally {
            refetchSubscription().then(() => {
                handleCloseModal();
            });
        }
    };

    const handlePickPlanSuccess = () => {
        navigate('../success', { relative: 'path' });
    };

    const handleDeleteOrganization = async () => {
        if (confirmationText !== 'DELETE') {
            toast({
                title: 'Confirmation required',
                description: 'Please type "DELETE" to confirm',
                variant: 'error',
            });
            return;
        }

        setIsDeleting(true);
        try {
            await getZaplifySdk().profiles.userAdmin.deleteOrganization(userId);
            toast({
                title: 'Workspace deleted',
                description: 'Your workspace has been permanently deleted',
                variant: 'success',
            });
            // Redirect to workspaces page
            await reloadClaims('none');
            window.location.href = paths.WORKSPACES.ROOT;
        } catch (error) {
            console.error('Failed to delete organization:', error);
            toast({
                title: 'Failed to delete workspace',
                description: 'Please try again or contact support',
                variant: 'error',
            });
        } finally {
            setIsDeleting(false);
            setIsDeleteOrganizationOpen(false);
            setConfirmationText('');
        }
    };

    return (
        <div className="space-y-4">
            <SubscriptionDetails
                subscription={subscriptionDetails}
                isLockedFromSelfService={isLockedFromSelfService}
                onUpgradePlan={() => handleShowModal('upgrade-plan')}
                onCancelPlan={() => handleShowModal('cancel-plan')}
                onRenewPlan={() => handleShowModal('renew-plan')}
            />

            <BillingDetails
                subscription={subscriptionDetails}
                onLoadSelfServicePortal={loadSelfServicePortal}
                refetchSubscription={refetchSubscription}
            />

            {/* Danger Zone - Only show for Global Admins */}
            {isGlobalAdmin && (
                <Card className="border-red-200 bg-red-50">
                    <CardHeader>
                        <CardTitle className="text-red-700 flex items-center gap-2">
                            <Trash2 className="h-5 w-5" />
                            Danger Zone
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="space-y-2">
                            <h4 className="font-medium text-red-700">Delete Workspace</h4>
                            <p className="text-sm text-red-600">
                                Permanently delete <span className="font-semibold">{organizationName}</span> and all
                                associated data. This action cannot be undone.
                            </p>
                            <Button
                                variant="destructive"
                                onClick={() => setIsDeleteOrganizationOpen(true)}
                                className="bg-red-600 hover:bg-red-700"
                            >
                                <Trash2 className="h-4 w-4 mr-2" />
                                Delete {organizationName}
                            </Button>
                        </div>
                    </CardContent>
                </Card>
            )}

            <CancelSubscriptionDialog
                isOpen={activeModal === 'cancel-plan'}
                onClose={handleCloseModal}
                onConfirm={handleCancelSubscription}
            />

            <RenewalSubscriptionDialog
                isOpen={activeModal === 'renew-plan'}
                onClose={handleCloseModal}
                onConfirm={handleRenewSubscription}
                renewalDate={
                    subscriptionDetails.subscription.renewalAt
                        ? new Date(subscriptionDetails.subscription.renewalAt).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric',
                          })
                        : ''
                }
            />

            <PickPlanDialog
                isOpen={activeModal === 'upgrade-plan'}
                onClose={handleCloseModal}
                onSuccess={handlePickPlanSuccess}
            />

            <SuccessDialog
                isOpen={activeModal === 'success'}
                onClose={() => {
                    Promise.all([reloadClaims(userOrganizationId), refetchSubscription()]).then(() => {
                        handleCloseModal();
                    });
                }}
            />

            {/* Delete Organization Confirmation Dialog */}
            <Dialog open={isDeleteOrganizationOpen} onOpenChange={setIsDeleteOrganizationOpen}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle className="text-red-700">Delete {organizationName}</DialogTitle>
                        <DialogDescription className="text-red-600">
                            This action cannot be undone. This will permanently delete{' '}
                            <span className="font-semibold">{organizationName}</span> and all associated data including
                            contacts, campaigns, and settings.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div>
                            <Label htmlFor="confirmation" className="text-sm font-medium">
                                Type <span className="font-bold text-red-700">DELETE</span> to confirm:
                            </Label>
                            <Input
                                id="confirmation"
                                value={confirmationText}
                                onChange={(e) => setConfirmationText(e.target.value)}
                                placeholder="DELETE"
                                className="mt-1"
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setIsDeleteOrganizationOpen(false)}>
                            Cancel
                        </Button>
                        <Button
                            variant="destructive"
                            onClick={handleDeleteOrganization}
                            disabled={confirmationText !== 'DELETE' || isDeleting}
                            className="bg-red-600 hover:bg-red-700"
                        >
                            {isDeleting ? (
                                <>
                                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                    Deleting...
                                </>
                            ) : (
                                <>
                                    <Trash2 className="h-4 w-4 mr-2" />
                                    Delete {organizationName}
                                </>
                            )}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default SubscriptionPage;
